/**
 * Branded "Add GIGGONE to Your Home Screen" modal.
 *
 * Two variants, chosen by src/lib/pwa.ts:
 *   - native  → Chromium fired `beforeinstallprompt`, so [Add App] opens the
 *               real browser install dialog.
 *   - ios     → iPhone / iPad Safari, which has no programmatic install. We
 *               show the exact Share → Add to Home Screen → Add steps and
 *               NEVER show an "Add App" button that could not work.
 *
 * Anti-spam and analytics live entirely in src/lib/pwa.ts so the rules can
 * never drift between components.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, Plus, Share, Smartphone, X } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import {
  decidePrompt,
  markDismissed,
  markPwaTrigger,
  markShown,
  onPwaChange,
  promptInstall,
  type PwaTrigger,
} from '@/lib/pwa';

/** Routes that count as a "logged-in moment" worth asking after. */
const DASHBOARD_RE = /^\/(en|es)\/(dashboard|panel)(\/|$)/;
// NOTE: the Spanish checkout-return route is `pago-completado` (App.tsx). The
// earlier pattern said `pago-completo`, so Spanish buyers never triggered the
// post-purchase prompt. Both spellings are accepted now.
const PURCHASE_RE = /^\/(en|es)\/(payment-complete|pago-completado|pago-completo)(\/|$)/;


const InstallPrompt: React.FC = () => {
  const { t, lang } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'native' | 'ios'>('native');
  const [trigger, setTrigger] = useState<PwaTrigger | null>(null);
  const [busy, setBusy] = useState(false);
  const shownThisSession = useRef(false);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  /** Ask pwa.ts whether this moment qualifies; open only if it says yes. */
  const evaluate = useCallback(() => {
    if (shownThisSession.current) return;
    const decision = decidePrompt();
    if (!decision.show) return;
    shownThisSession.current = true;
    setMode(decision.mode);
    setTrigger(decision.trigger);
    setOpen(true);
    markShown(decision.trigger);
  }, []);

  // Diagnostics: /pwa-check's "Force show" clears the per-view guard so the
  // modal can be re-opened on the device under test. decidePrompt() still
  // owns every real anti-spam rule, so this cannot spam a normal visitor.
  useEffect(() => {
    const win = window as unknown as { __ggPwaReopen?: () => void };
    win.__ggPwaReopen = () => {
      shownThisSession.current = false;
    };
    return () => {
      delete win.__ggPwaReopen;
    };
  }, []);


  // Route-based triggers: a dashboard visit and a completed lead purchase.
  useEffect(() => {
    const p = location.pathname;
    if (PURCHASE_RE.test(p)) markPwaTrigger('lead_purchased');
    else if (DASHBOARD_RE.test(p)) markPwaTrigger('dashboard');
    // Delay so the page paints first — the modal should feel like a reward,
    // not an interruption on top of a loading screen.
    const id = window.setTimeout(evaluate, 1600);
    return () => window.clearTimeout(id);
  }, [location.pathname, evaluate]);

  // Action triggers (saved alert, posted project) and late-arriving
  // beforeinstallprompt events both notify through this subscription.
  useEffect(() => onPwaChange(() => window.setTimeout(evaluate, 400)), [evaluate]);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    markDismissed(trigger);
  }, [trigger]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const handleAdd = async () => {
    setBusy(true);
    // promptInstall() records pwa_install_accepted, and on "dismissed" it also
    // starts the 30-day cooldown — so nothing extra is needed here.
    await promptInstall(trigger);
    setBusy(false);
    setOpen(false);
  };


  if (!open) return null;

  const steps = [
    { icon: Share, text: t('pwa.iosStep1') },
    { icon: Plus, text: t('pwa.iosStep2') },
    { icon: Download, text: t('pwa.iosStep3') },
  ];

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pwa-title"
      aria-describedby="pwa-desc"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
        {/* Brand header */}
        <div className="relative flex items-center gap-3 bg-slate-900 px-5 py-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-500">
            <Smartphone className="h-6 w-6 text-white" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-orange-400">
              {t('pwa.eyebrow')}
            </p>
            <h2 id="pwa-title" className="truncate text-base font-bold text-white sm:text-lg">
              {t('pwa.title')}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            aria-label={t('common.close')}
            className="ml-auto rounded-lg p-1.5 text-slate-300 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-400"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-5 py-5">
          <p id="pwa-desc" className="text-sm leading-relaxed text-slate-600">
            {t('pwa.description')}
          </p>

          {mode === 'ios' ? (
            <>
              <ol className="mt-5 space-y-3">
                {steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-white">
                      {i + 1}
                    </span>
                    <span className="flex min-w-0 items-center gap-2 pt-1 text-sm font-medium text-slate-800">
                      <s.icon className="h-4 w-4 shrink-0 text-orange-500" aria-hidden="true" />
                      <span>{s.text}</span>
                    </span>
                  </li>
                ))}
              </ol>
              <button
                type="button"
                onClick={close}
                className="mt-5 w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                {t('pwa.iosDone')}
              </button>
              <p className="mt-3 text-center text-xs text-slate-500">{t('pwa.iosNote')}</p>
            </>
          ) : (
            <>
              <ul className="mt-4 space-y-2">
                {[t('pwa.benefit1'), t('pwa.benefit2'), t('pwa.benefit3')].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" aria-hidden="true" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={busy}
                  className="flex-1 rounded-xl bg-orange-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  {busy ? t('common.loading') : t('pwa.add')}
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  {t('pwa.notNow')}
                </button>
              </div>
              <p className="mt-3 text-center text-xs text-slate-500">{t('pwa.freeNote')}</p>
            </>
          )}
        </div>
        <span className="sr-only">{lang}</span>
      </div>
    </div>
  );
};

export default InstallPrompt;
